/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from 'react';
import { ExecuteProcessForm, ExecuteProcessFormProps } from 'ogc-processes-components';
import type { FormContextType, FormValidation } from 'ogc-processes-components';
import { useTranslation } from 'react-i18next';

export const AntFlowExecuteProcessForm: React.FC<ExecuteProcessFormProps> = (props) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();

  const hasLauncher = Boolean(props.inputs?.['antflow_config:launcher']);

  const customValidate = (formData: FormContextType, errors: FormValidation): FormValidation => {
    if (!Object.hasOwn(formData, 'antflow_config:launcher')) {
      errors.addError('Launcher selection is required. Please select a launcher to proceed.');
    }
    return errors;
  };

  const FR_LauncherGuide = (
    <div>
      <p>
        Aucun launcher n'est disponible pour exécuter ce workflow. En général, cela signifie qu'aucun environnement
        d'exécution ne correspond aux ressources (flavors) requises par ce workflow. Veuillez contacter un
        administrateur afin qu'il mette à disposition un launcher adapté ou configure les règles nécessaires à
        l'exécution de ce workflow (les flavors requis).
      </p>
      <p>
        Le cas échéant, vous pouvez également créer un launcher local depuis votre menu: Profil &rarr;{' '}
        {t(['launchers.title', ''])} &rarr; {t(['launchers.new', 'new'])}.
      </p>
      <p>
        Si vous disposez déjà d'un launcher, démarrez-le avec la commande&nbsp;:{' '}
        <code className="bg-gray-900 p-2 text-white border-round-md">antflow-launcher run</code>.
      </p>
    </div>
  );

  const EN_LauncherGuide = (
    <div>
      <p>
        No launcher is available to run this workflow. This usually means no execution environment matches the resources
        (flavors) this workflow requires. Please contact an administrator to configure the rules needed to execute it
        (the required flavors).
      </p>
      <p>
        If applicable, you can also create a local launcher from your menu: Profile &rarr; {t(['launchers.title', ''])}{' '}
        &rarr; {t(['launchers.new', 'new'])}.
      </p>
      <p>
        If you already have a launcher, start it with the command&nbsp;:{' '}
        <code className="bg-gray-900 p-2 text-white border-round-md">antflow-launcher run</code>.
      </p>
    </div>
  );

  if (!hasLauncher) {
    return (
      <div className="p-3 text-bluegray-800 flex flex-column gap-3 bg-orange-50 border-round-md border-1 border-orange-100">
        <div className="flex justify-content-center">
          <i style={{ fontSize: '3rem' }} className="pi pi-exclamation-triangle text-orange-500" />
        </div>
        {language === 'fr' ? FR_LauncherGuide : EN_LauncherGuide}
      </div>
    );
  }

  return <ExecuteProcessForm {...props} customValidate={customValidate} />;
};
